import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, LinkedinLogo } from "@phosphor-icons/react";
import { Reveal } from "./blocks";

const STR = {
  en: {
    eyebrow: "Leadership",
    title: "Built by an engineer and a clinician.",
    sub: "One writes the code. One lives the problems it solves.",
    more: (n) => `+${n} more`,
    less: "Show less",
    facts: ["15+ years combined experience", "Technology + healthcare + psychology", "Building from Noida and Gorakhpur"],
    story: "Our story",
    linkedin: "LinkedIn",
    people: [
      {
        id: "ajit",
        name: "Ajit Kumar",
        role: "Founder & CEO",
        img: "/founder-ajit.jpg", w: 640, h: 691,
        linkedin: "https://www.linkedin.com/in/ajit-rai-aj280295",
        chips: ["B.Tech Computer Science", "MBA, IT & Project Management", "MA Psychology", "Oracle Certified Java Professional", "8+ yrs engineering", "Ex-LTIMindtree", "Ex-QuinStreet"],
        bio: "Builds the product and leads engineering. Brings scale-grade engineering, and a psychologist's eye for how people actually use software.",
      },
      {
        id: "priyangu",
        name: "Dr. Priyangu Rai",
        role: "Co-founder, COO & MD",
        img: "/founder-priyangu.jpg", w: 640, h: 786,
        chips: ["BPT", "MPT, Musculoskeletal Sciences (RGUHS)", "Member, IAP", "Certified Manual Therapist", "Ex-Manipal Hospital, Pune", "Ex-ESI Hospital, Bangalore", "Founder, Priyajit Ortho Neuro Rehabilitation Center"],
        bio: "Runs a working rehabilitation centre and has treated 1000+ patients. Shapes every clinical screen we ship.",
      },
    ],
  },
  hi: {
    eyebrow: "नेतृत्व",
    title: "एक इंजीनियर और एक चिकित्सक ने मिलकर बनाया।",
    sub: "एक कोड लिखता है। एक उन समस्याओं को रोज़ जीता है जिन्हें वह कोड हल करता है।",
    more: (n) => `+${n} और`,
    less: "कम दिखाएँ",
    facts: ["15+ वर्षों का संयुक्त अनुभव", "टेक्नोलॉजी + हेल्थकेयर + मनोविज्ञान", "नोएडा और गोरखपुर से निर्माण"],
    story: "हमारी कहानी",
    linkedin: "LinkedIn",
    people: [
      {
        id: "ajit",
        name: "अजित कुमार",
        role: "फ़ाउंडर व CEO",
        img: "/founder-ajit.jpg", w: 640, h: 691,
        linkedin: "https://www.linkedin.com/in/ajit-rai-aj280295",
        chips: ["B.Tech कंप्यूटर साइंस", "MBA, IT व प्रोजेक्ट मैनेजमेंट", "MA मनोविज्ञान", "Oracle Certified Java Professional", "8+ वर्ष इंजीनियरिंग", "Ex-LTIMindtree", "Ex-QuinStreet"],
        bio: "प्रोडक्ट बनाते हैं और इंजीनियरिंग का नेतृत्व करते हैं। स्केल-स्तर की इंजीनियरिंग, और यह समझ कि लोग सॉफ्टवेयर असल में कैसे इस्तेमाल करते हैं।",
      },
      {
        id: "priyangu",
        name: "डॉ. प्रियंगु राय",
        role: "को-फ़ाउंडर, COO व MD",
        img: "/founder-priyangu.jpg", w: 640, h: 786,
        chips: ["BPT", "MPT, मस्कुलोस्केलेटल साइंसेज़ (RGUHS)", "सदस्य, IAP", "Certified Manual Therapist", "Ex-मणिपाल हॉस्पिटल, पुणे", "Ex-ESI हॉस्पिटल, बेंगलुरु", "फ़ाउंडर, प्रियाजित ऑर्थो न्यूरो रिहैबिलिटेशन सेंटर"],
        bio: "एक चालू रिहैबिलिटेशन सेंटर चलाती हैं और 1000+ मरीज़ों का इलाज कर चुकी हैं। हर क्लिनिकल स्क्रीन उन्हीं के अनुभव से बनती है।",
      },
    ],
  },
};

const PersonCard = ({ p, s }) => {
  const [open, setOpen] = useState(false);
  const chips = p.chips.slice(0, 8);
  const hidden = chips.length - 4;

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[20px] border border-navy/[0.08] bg-white lg:min-h-[35rem]" data-testid={`leader-card-${p.id}`}>
      <div className="relative h-72 overflow-hidden bg-mist lg:h-[20rem]">
        <img
          src={p.img}
          alt={p.name}
          width={p.w}
          height={p.h}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.03]"
        />
        <div aria-hidden className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white to-transparent" />
      </div>

      <div className="flex flex-1 flex-col px-6 pb-7 pt-2 lg:px-7">
        <h3 className="font-display text-[1.75rem] font-bold leading-tight tracking-[-0.02em] text-navy">{p.name}</h3>
        <p className="mt-1 text-sm font-bold uppercase tracking-[0.12em] text-electric-ink">{p.role}</p>

        <div className="mt-5 flex flex-wrap gap-1.5">
          {chips.map((c, i) => (
            <span
              key={c}
              className={`rounded-full border border-navy/12 bg-mist px-2.5 py-1 text-[11px] font-semibold text-navy/80 ${i > 3 && !open ? "hidden sm:inline-block" : ""}`}
              data-testid={`leader-chip-${p.id}-${i}`}
            >
              {c}
            </span>
          ))}
          {hidden > 0 && (
            <button
              onClick={() => setOpen((o) => !o)}
              className="rounded-full border border-electric-ink/30 px-2.5 py-1 text-[11px] font-bold text-electric-ink sm:hidden"
              data-testid={`leader-chips-toggle-${p.id}`}
            >
              {open ? s.less : s.more(hidden)}
            </button>
          )}
        </div>

        <p className="mt-5 text-[15px] leading-relaxed text-ink">{p.bio}</p>

        {p.linkedin && (
          <a
            href={p.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${p.name} — LinkedIn`}
            className="mt-auto inline-flex w-fit items-center gap-2 pt-6 text-sm font-bold text-electric-ink"
            data-testid={`leader-linkedin-${p.id}`}
          >
            <LinkedinLogo size={18} weight="fill" /> {s.linkedin}
          </a>
        )}
      </div>
    </article>
  );
};

export const Leadership = ({ lang = "en", heading = true, storyLink = true }) => {
  const s = STR[lang];
  return (
    <div data-testid="leadership">
      {heading && (
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-electric-ink">{s.eyebrow}</p>
          <h2 className="mt-4 max-w-2xl font-display text-[1.75rem] font-bold leading-tight tracking-[-0.02em] text-navy lg:text-[2.5rem]">{s.title}</h2>
          <p className="mt-4 max-w-xl text-[17px] text-ink">{s.sub}</p>
        </Reveal>
      )}

      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:max-w-[64rem]">
        {s.people.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.08}><PersonCard p={p} s={s} /></Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-2.5 border-t border-navy/10 pt-6" data-testid="leadership-facts">
          {s.facts.map((f) => (
            <span key={f} className="flex items-center gap-2 text-[13px] font-semibold text-navy/75">
              <span className="h-1.5 w-1.5 rounded-full bg-teal" /> {f}
            </span>
          ))}
          {storyLink && (
            <Link to="/about" className="group ml-auto inline-flex items-center gap-2 text-sm font-bold text-electric-ink" data-testid="leadership-story-cta">
              {s.story} <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          )}
        </div>
      </Reveal>
    </div>
  );
};
